import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { MutationConfig, PaginationResponse, useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import { ItemEntity } from '@/types/api';

import { getItemQueryKey } from './get-item';
import { getItemsQueryKey } from './get-items';

export const itemLockToggle = async (
  orgId: string,
  projectId: string,
  itemId: string,
  lock: boolean,
): Promise<void> => {
  await api.post<undefined, ApiResponse<undefined>>(
    `/orgs/${orgId}/projects/${projectId}/items/${itemId}/${lock ? 'lock' : 'unlock'}`,
  );
};

export interface UseItemLockToggleOptions {
  mutationConfig?: MutationConfig<(lock: boolean) => Promise<void>>;
}

export const useItemLockToggle = (
  itemId: string,
  { mutationConfig }: UseItemLockToggleOptions = {},
) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { orgId, projectId } = useScopeIds('org', 'project');

  const { onSuccess, onError, ...restConfig } = mutationConfig ?? {};

  return useMutation({
    onSuccess: (_, lock, ...args) => {
      // update item in cache instead of re-fetching everything
      queryClient.setQueriesData(
        { queryKey: getItemsQueryKey(orgId, projectId) },
        (old: InfiniteData<PaginationResponse<ItemEntity>> | undefined) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((pagination: PaginationResponse<ItemEntity>) => {
              return {
                ...pagination,
                data: pagination.data.map((item) =>
                  item.id === itemId ? { ...item, locked: lock } : item,
                ),
              };
            }),
          };
        },
      );
      void queryClient.invalidateQueries({
        queryKey: getItemQueryKey(orgId, projectId, itemId),
      });
      notifications.success('items.lock.success');
      onSuccess?.(_, lock, ...args);
    },
    onError: (...args) => {
      notifications.error('items.lock.error');
      onError?.(...args);
    },
    ...restConfig,
    mutationFn: (lock: boolean) => itemLockToggle(orgId, projectId, itemId, lock),
  });
};
